import { useCallback, useEffect, useState } from 'react';
import { mockDataService } from '../services/mockDataService';
import { WordPressCategory, WordPressPost, wordpressService } from '../services/wordpressService';

export interface UseDataServiceReturn {
  // Posts data
  posts: WordPressPost[];
  loading: boolean;
  error: string | null;
  
  // Categories data
  categories: WordPressCategory[];
  categoriesLoading: boolean;
  categoriesError: string | null;
  
  // Pagination
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  
  // Actions
  fetchPosts: (params?: {
    page?: number;
    categories?: number[];
    search?: string;
    orderby?: string;
    order?: 'asc' | 'desc';
  }) => Promise<void>;
  
  fetchCategories: () => Promise<void>;
  
  loadMorePosts: () => Promise<void>;
  
  searchPosts: (searchTerm: string) => Promise<void>;
  
  filterByCategory: (categoryId: number | null) => Promise<void>;
  
  // Connection testing
  testConnection: () => Promise<{ success: boolean; message: string; status?: number }>;
  
  // State
  activeCategory: number | null;
  searchTerm: string;
  
  // Data source info
  dataSource: 'wordpress' | 'mock';
}

export const useDataService = (initialPostsPerPage: number = 10): UseDataServiceReturn => {
  // Determine data source based on environment
  const isDevelopment = process.env.NODE_ENV === 'development';
  const useMockData = process.env.REACT_APP_USE_MOCK_DATA === 'true' || 
                     (isDevelopment && !process.env.REACT_APP_WORDPRESS_URL);
  
  const dataSource: 'wordpress' | 'mock' = useMockData ? 'mock' : 'wordpress';
  
  // Posts state
  const [posts, setPosts] = useState<WordPressPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Categories state
  const [categories, setCategories] = useState<WordPressCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  
  // Filter state
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Get the appropriate service
  const getService = () => {
    return useMockData ? mockDataService : wordpressService;
  };
  
  // Fetch posts from the appropriate service
  const fetchPosts = useCallback(async (params: {
    page?: number;
    categories?: number[];
    search?: string;
    orderby?: string;
    order?: 'asc' | 'desc';
  } = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const service = getService();
      const fetchedPosts = await service.getPosts({
        page: params.page || 1,
        per_page: initialPostsPerPage,
        categories: params.categories || (activeCategory ? [activeCategory] : undefined),
        search: params.search || searchTerm,
        orderby: params.orderby || 'date',
        order: params.order || 'desc'
      });
      
      if (params.page && params.page > 1) {
        // Append to existing posts for pagination
        setPosts(prevPosts => [...prevPosts, ...fetchedPosts]);
      } else {
        // Replace posts for new search/filter
        setPosts(fetchedPosts);
        setCurrentPage(1);
      }
      
      // Check if there are more pages
      if (useMockData) {
        const totalCount = mockDataService.getTotalCount({
          categories: params.categories || (activeCategory ? [activeCategory] : undefined),
          search: params.search || searchTerm
        });
        const totalPages = Math.ceil(totalCount / initialPostsPerPage);
        setTotalPages(totalPages);
        setHasMore((params.page || 1) < totalPages);
      } else {
        setHasMore(fetchedPosts.length === initialPostsPerPage);
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  }, [activeCategory, searchTerm, initialPostsPerPage, useMockData]);
  
  // Fetch categories from the appropriate service
  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      setCategoriesError(null);
      
      const service = getService();
      const fetchedCategories = await service.getCategories();
      setCategories(fetchedCategories);
      
    } catch (err) {
      setCategoriesError(err instanceof Error ? err.message : 'Failed to fetch categories');
    } finally {
      setCategoriesLoading(false);
    }
  }, [useMockData]);
  
  // Load more posts (pagination)
  const loadMorePosts = useCallback(async () => {
    if (loading || !hasMore) return;
    
    const nextPage = currentPage + 1;
    await fetchPosts({ page: nextPage });
    setCurrentPage(nextPage);
  }, [loading, hasMore, currentPage, fetchPosts]);
  
  // Search posts
  const searchPosts = useCallback(async (term: string) => {
    setSearchTerm(term);
    setActiveCategory(null); // Clear category filter when searching
    
    if (term.trim()) {
      await fetchPosts({ search: term, page: 1 });
    } else {
      await fetchPosts({ page: 1 });
    }
  }, [fetchPosts]);
  
  // Filter by category
  const filterByCategory = useCallback(async (categoryId: number | null) => {
    setActiveCategory(categoryId);
    setSearchTerm(''); // Clear search when filtering by category
    setCurrentPage(1);
    
    await fetchPosts({ 
      categories: categoryId ? [categoryId] : undefined,
      page: 1 
    });
  }, [fetchPosts]);
  
  // Test connection
  const testConnection = useCallback(async () => {
    const service = getService();
    return service.testConnection();
  }, [useMockData]);
  
  // Initial data fetch
  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [fetchPosts, fetchCategories]);
  
  return {
    // Posts data
    posts,
    loading,
    error,
    
    // Categories data
    categories,
    categoriesLoading,
    categoriesError,
    
    // Pagination
    currentPage,
    totalPages,
    hasMore,
    
    // Actions
    fetchPosts,
    fetchCategories,
    loadMorePosts,
    searchPosts,
    filterByCategory,
    
    // Connection testing
    testConnection,
    
    // State
    activeCategory,
    searchTerm,
    
    // Data source info
    dataSource
  };
};
